import type { jsPDFOptions } from 'jspdf';
import { jsPDF } from 'jspdf';
import type {
  BankAccountInfosType,
  Base64ImageType,
  TransactionInfosType,
  HeaderFilter,
  ExportCellFormatters
} from '@/types';
import { getFormattedDate } from '@/utils/moment';
import LemonwayLogo from '@/assets/img/logos/lemonway-compressed.jpeg';
import CeoSignature from '@/assets/img/logos/ceo-signature.png';
import '@/assets/fonts/pdf/PT_Sans-normal';
import '@/assets/fonts/pdf/PT_Sans-bold';
import '@/assets/fonts/pdf/PT_Sans-italic';
import '@/assets/fonts/pdf/Montserrat-bold';
import '@/assets/fonts/pdf/Lato-Regular-normal';
import palette from '~/assets/palette';
import { useWalletsStore } from '~/stores/wallets/wallets-store';
import useWalletsApi from '~/composables/api/useWalletsApi';

export const usePdfViewer = () => {
  const { $i18n } = useNuxtApp();
  const { t, locale, n } = $i18n;
  const { calculateBlockingReasonDisplay } = useWalletsStore();
  const { getWallet } = useWalletsApi();
  const { viewPdf, src, pdfName } = storeToRefs(usePdfViewerStore());
  const { getDisplayExpr, toPascalCaseName } = useUtils();
  const currency = localStorage.getItem('partnerCurrency') || 'EUR';
  const partnerName = localStorage.getItem('partnerName');

  const spacer3 = 16;
  const spacer1 = spacer3 * 0.25;
  const spacer2 = spacer3 * 0.5;
  const spacer4 = spacer3 * 1.5;
  const spacer5 = spacer3 * 3;
  const darkGrey = '#575B77';
  const green = '#57ED7D';
  const currencyDataFields = [
    'amount',
    'balance',
    'commission',
    'MinThresholdNet',
    'MaxThresholdNet',
    'FeeFixed',
    'FromMerchantFees',
    'senderBalance',
    'receiverBalance',
    'commissionAmount',
    'AmountTTCStr',
    'AmountComStr'
  ];

  const fontSize = 14;
  const marginX = spacer5;
  const marginY = spacer4;

  let doc: jsPDF = new jsPDF();
  let logo: Base64ImageType;
  let pageWidth = 0;
  let pageHeight = 0;
  let cursorY = 0;

  const setTypo = (font: string, style: string, size: number, color: string) => {
    doc?.setFont(font, style);
    doc?.setFontSize(size);
    doc?.setTextColor(color);
  };
  const getFullName = (data: any) => {
    const name = data.Name?.trim?.();
    if (name) return toPascalCaseName(name);

    const first = data.clientFirstName || data.FirstName || '';
    const last = data.clientLastName || data.LastName || '';

    return `${toPascalCaseName(first)} ${toPascalCaseName(last)}`.trim();
  };

  const getExportable = (
    rows: Array<any>,
    columns: Array<any>,
    machineReadable: boolean = false,
    formatters?: ExportCellFormatters
  ) => {
    const header = columns.map((column) => column.caption);

    const body = rows.map((row) =>
      columns?.map((column) => {
        const formatColumn = formatters?.[column.name];
        if (formatColumn) return formatColumn(row);

        let displayExpr = row[column.dataField];
        if (column.dataField === 'clientFirstName') {
          displayExpr = getFullName(row);
        } else if (column.name === 'wallet-type') {
          displayExpr = t(
            `transactions.status.walletType.${displayExpr === 16 ? 'technicalWallet' : 'paymentWallet'}`
          );
        } else if (displayExpr == null) {
          displayExpr = '';
        } else if (column.headerFilter) {
          const val = (column.headerFilter.dataSource as Array<HeaderFilter>).find(
            (filter) => filter.value === displayExpr
          );
          displayExpr = val ? val.text : displayExpr;
        }

        if (!machineReadable) {
          switch (column.dataType) {
            case 'datetime':
              displayExpr = getFormattedDate(displayExpr, 'L LTS', locale.value);
              break;

            case 'date':
              displayExpr = getFormattedDate(displayExpr, 'L', locale.value);
              break;

            case 'boolean':
              if (column.name === 'blocked') {
                displayExpr = t(`common.${row[column.dataField]}`);
                break;
              }
              displayExpr = t(`common.${displayExpr}`);
              break;

            default:
              if (
                currencyDataFields.includes(column?.dataField) ||
                (column?.format && column?.format?.type === 'currency')
              ) {
                displayExpr = n(!displayExpr ? 0 : displayExpr, {
                  style: 'currency',
                  currency,
                  currencyDisplay: 'narrowSymbol'
                });
              } else if (column?.dataField === 'BlockingReasons') {
                displayExpr = calculateBlockingReasonDisplay({ BlockingReasons: displayExpr });
              } else if (column?.dataField === 'situationId' && displayExpr === 16) {
                displayExpr = t('wallets.status.tech');
              }
              break;
          }
        }

        // Avoid null/undefined (but keep valid falsy values like 0)
        displayExpr = displayExpr ?? '';
        return displayExpr;
      })
    );

    return { header, body };
  };

  const getBase64Image = (url: string, scale = 1): Promise<Base64ImageType> => {
    return new Promise((resolve) => {
      const img = new Image();
      img.src = url;

      img.onload = () => {
        // Create an empty canvas element
        const canvas = document.createElement('canvas');
        canvas.width = img.width;
        canvas.height = img.height;

        // Copy the image contents to the canvas
        const context = canvas.getContext('2d');
        context?.drawImage(img, 0, 0);

        resolve({
          data: canvas.toDataURL('image/png'),
          width: canvas.width * scale,
          height: canvas.height * scale
        });
      };
    });
  };

  const init = async (options: jsPDFOptions = {}) => {
    doc = new jsPDF({ ...options, unit: 'px' });
    doc.setFont('Lato-Regular', 'normal');
    pageWidth = doc.internal.pageSize.getWidth();
    pageHeight = doc.internal.pageSize.getHeight();
    // Logo
    logo = await getBase64Image(LemonwayLogo, 0.2);
  };

  const toggleScroll = (disable: boolean) => {
    if (disable) {
      document.body.classList.add('no-scroll'); // Disable scrolling
    } else {
      document.body.classList.remove('no-scroll'); // Re-enable scrolling
    }
  };
  const addHeader = () => {
    // Initial cursorY
    cursorY = marginY;

    doc.addImage(
      logo.data,
      'PNG',
      pageWidth / 2 - logo.width / 2, // Center the logo
      cursorY,
      logo.width,
      logo.height,
      '',
      'SLOW'
    );

    // Move cursorY
    cursorY += logo.height;
  };

  const addFooter = () => {
    // Footer
    const websiteUrl = 'https://www.lemonway.com';

    let cursorYFooter = pageHeight - marginY - spacer4;

    setTypo('PT_Sans', 'normal', fontSize * 0.5, darkGrey);
    doc.text(
      [
        t('pdfTemplate.common.footer1'),
        t('pdfTemplate.common.footer2'),
        t('pdfTemplate.common.footer3')
      ],
      marginX,
      cursorYFooter
    );

    cursorYFooter += fontSize * 0.5 + spacer2 + spacer1;
    setTypo('PT_Sans', 'normal', fontSize * 0.5, green);
    doc.textWithLink(websiteUrl, marginX, cursorYFooter, {
      url: websiteUrl
    });

    setTypo('PT_Sans', 'normal', fontSize * 0.5, darkGrey);
    doc.text(
      t('pdfTemplate.common.footer4'),
      marginX + doc.getTextWidth(websiteUrl),
      cursorYFooter
    );
  };

  const rowText = (row: Array<any>, columnWidth: number, rowHeight: number) => {
    setTypo('PT_Sans', 'bold', fontSize * 0.7, darkGrey);

    let cursorX = marginX;
    row.forEach((cell) => {
      let cellString = cell.toString();
      if (cellString.length > 58) {
        cellString = cellString.substring(0, 56) + '...';
      }
      doc.rect(cursorX, cursorY, columnWidth, rowHeight);
      doc.text(cellString, cursorX + spacer1, cursorY + spacer1, {
        baseline: 'top',
        maxWidth: columnWidth - spacer1
      });
      cursorX += columnWidth;
    });
  };

  const exportToPdf = async (
    rows: Array<any>,
    columns: Array<any>,
    fileName: string,
    rowsPerPage: number,
    formatters?: ExportCellFormatters
  ) => {
    const { header, body } = getExportable(rows, columns, false, formatters);

    await init({ orientation: 'landscape', format: 'a3' });

    // First page (even if no data)
    addHeader();
    addFooter();

    cursorY += spacer3;

    // With margins
    const columnWidth = (pageWidth - 2 * marginX) / header.length;
    // With header, footer and margins
    const rowHeight = (pageHeight - cursorY - marginY - spacer5) / (rowsPerPage + 1);

    // Header row
    rowText(header, columnWidth, rowHeight);

    body.forEach((row, index) => {
      // If new page
      if (index > 0 && index % rowsPerPage === 0) {
        doc.addPage();

        addHeader();
        addFooter();

        // Header row
        cursorY += spacer3;
        rowText(header, columnWidth, rowHeight);
      }

      // Row
      cursorY += rowHeight;
      rowText(row, columnWidth, rowHeight);
    });

    viewPdf.value = true;
    src.value = URL.createObjectURL(doc.output('blob'));
    pdfName.value = `${fileName}.pdf`;
  };

  const generatePdfFromTemplate = async (element: HTMLElement) => {
    toggleScroll(true);
    await init();

    const scale = pageWidth / element.offsetWidth;

    await doc.html(element, {
      html2canvas: { scale }
    });

    return doc.output('blob');
  };

  const generateTransactionPdf = async (
    type: string,
    transactionData: TransactionInfosType,
    bankAccountData?: BankAccountInfosType
  ) => {
    await init();

    addHeader();

    // Lemonway's name
    cursorY += spacer5;
    setTypo('Montserrat', 'bold', fontSize, palette.green);
    doc.text('Lemonway SAS', marginX, cursorY);

    // Lemonway's description
    cursorY += spacer3;
    setTypo('Montserrat', 'normal', fontSize, palette.darkGrey);
    doc.text('Your payment factory', marginX, cursorY);

    // Lemonway's address
    cursorY += spacer3;
    setTypo('PT_Sans', 'normal', fontSize, palette.darkGrey);
    doc.text(['8 rue du Sentier', '75002 Paris', 'France'], marginX, cursorY);

    // City and date
    cursorY += spacer5;
    setTypo('PT_Sans', 'italic', fontSize, palette.darkGrey);
    doc.text(
      `Paris, ${t('pdfTemplate.common.date', {
        date: getFormattedDate(Date(), 'LL', locale.value)
      })}`,
      pageWidth - marginX,
      cursorY,
      {
        align: 'right'
      }
    );

    cursorY += spacer5;
    setTypo('PT_Sans', 'bold', fontSize, palette.darkGrey);
    const subject = t(`pdfTemplate.${type}.subject`, {
      id: transactionData.id,
      partnerName: transactionData.partnerName
    });
    doc.text(
      `${t('pdfTemplate.common.subject')}${t('common.colon')} ${subject}`,
      marginX,
      cursorY,
      {
        maxWidth: pageWidth - 2 * marginX // Wrap the paragraph
      }
    );

    setTypo('PT_Sans', 'normal', fontSize, palette.darkGrey);
    cursorY += spacer5;
    doc.text(`${t('pdfTemplate.common.bodyOpening')}${t('common.colon')} `, marginX, cursorY);

    cursorY += spacer4;
    let body = '';

    if (type === 'MoneyOutPdfConfirm') {
      body = t('pdfTemplate.moneyOutPdfConfirm.body', {
        amount: n(transactionData.amount, {
          style: 'currency',
          currency,
          currencyDisplay: 'narrowSymbol'
        }),
        accountNumber: bankAccountData?.iban_ciban,
        partnerName: transactionData.partnerName,
        date: getFormattedDate(transactionData.date, 'L LTS', locale.value)
      });
    } else if (type === 'proofPaymentPdf') {
      const baseBodyParams = {
        amount: n(transactionData.amount, {
          style: 'currency',
          currency,
          currencyDisplay: 'narrowSymbol'
        }),
        paymentMethod: getDisplayExpr('paymentMethods', transactionData.paymentMethod).replace(
          / \(\d+\)$/,
          ''
        ),
        date: getFormattedDate(transactionData.date, 'L LTS', locale.value),
        settlementDate: getFormattedDate(transactionData.settlementDate, 'L LTS', locale.value)
      };

      if (transactionData.walletIntId === 11) {
        body = t('pdfTemplate.proofPaymentPdf.scWalletbody', {
          ...baseBodyParams,
          customer: partnerName
        });
      } else {
        const walletData = await getWallet(transactionData.walletIntId);
        const fullName = `${walletData.Wallet.clientFirstName} ${walletData.Wallet.clientLastName}`;
        body = t('pdfTemplate.proofPaymentPdf.body', {
          ...baseBodyParams,
          name: walletData.Wallet.isCompany
            ? `${walletData.Wallet.companyName}, ${t('pdfTemplate.proofPaymentPdf.representedBy')} ${fullName},`
            : fullName
        });
      }
    }

    doc.text(body, marginX, cursorY, {
      align: 'justify',
      maxWidth: pageWidth - 2 * marginX // Wrap the paragraph
    });

    cursorY += 2 * spacer5;
    doc.text(t('pdfTemplate.common.bodyEnding'), marginX, cursorY);

    // CEO signature
    cursorY += spacer4;
    setTypo('PT_Sans', 'italic', fontSize, palette.darkGrey);
    doc.text('Chief executive officer', pageWidth - marginX, cursorY, {
      align: 'right'
    });

    cursorY += spacer3;
    setTypo('PT_Sans', 'bold', fontSize, palette.darkGrey);
    doc.text('Antoine Orsini', pageWidth - marginX, cursorY, {
      align: 'right'
    });

    cursorY += spacer3;
    const signature = await getBase64Image(CeoSignature, 0.35);

    doc.addImage(
      signature.data,
      'PNG',
      pageWidth - marginX - signature.width,
      cursorY,
      signature.width,
      signature.height,
      '',
      'SLOW'
    );
    cursorY += signature.height;

    // Footer
    addFooter();

    return doc.output('blob');
  };

  return {
    exportToPdf,
    generatePdfFromTemplate,
    generateTransactionPdf,
    toggleScroll
  };
};
